// 미디어 센터 본문 — /blog(전체 탭)와 /blog/:section 이 같은 화면을 공유한다.
//
// 레이아웃: masthead → 섹션 탭 → 툴바(검색·정렬) → hero 1편 → 3-col 그리드 → 무한 스크롤.
// 탭은 <Link> 라 누르면 URL 이 바뀐다(프리렌더된 섹션 페이지가 그대로 살아 있다 — mediaSections.js).
//
// 호출 규약: 섹션이 바뀌면 이 컴포넌트는 반드시 remount 돼야 한다.
//   /blog/a → /blog/b 는 같은 라우트 엘리먼트라 remount 가 저절로 일어나지 않는다.
//   그래서 BlogSectionPage 가 key={section} 을 준다. effect 로 state 를 되돌리지 않는 이유:
//   effect 는 커밋 뒤에 돌아서, 섹션이 바뀐 첫 렌더 한 번은 새 섹션 글에 '이전 섹션에서
//   치던 검색어'가 걸린 채로 그려진다(결과 0건이면 빈 상태가 반짝인다).
//
// TODO(backend):
//   - 검색·정렬·페이징이 전부 클라이언트 사이드다. 글 50편+ 부터 서버 페이징으로 전환.
//   - URL query(q, sort) 동기화가 없다 → 검색 상태가 공유 링크에 안 실린다.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  blogPosts,
  blogSections,
  getBlogPostsBySection,
  getBlogSection,
  searchPosts,
  sortPosts,
} from '../../data/blogPosts';
import { Seo } from '../../components/Seo';
import { Eyebrow } from '../../components/atoms/Eyebrow/Eyebrow';
import { MediaTabs } from './MediaTabs';
import { HeroPost, PostCard } from './PostCard';
import { LoadMoreSentinel } from './LoadMoreSentinel';
import styles from './MediaCenter.module.css';

const PAGE_SIZE = 6;
const SEARCH_DEBOUNCE_MS = 300;

/** 섹션이 없으면(= 전체 탭) 허브 문구, 있으면 섹션 문구를 쓴다. */
function getHeading(section) {
  if (!section) {
    return {
      eyebrow: 'Medi-Lawyer · Media',
      title: (
        <>
          의료와 법률,
          <br />
          현장의 기록
        </>
      ),
      lede: '실제 사건의 사실관계와 판결, 그리고 메디로이어가 보는 의료법률의 오늘. 변호사가 직접 작성합니다.',
      seoTitle: '미디어 센터 — 메디로이어',
      seoDescription:
        '의료사고·분쟁·손해배상 업무 사례와 전문가 칼럼. 의료 현장에서 실제로 다툰 쟁점을 변호사가 정리했습니다.',
      path: '/blog',
    };
  }

  return {
    eyebrow: `${section.group === '업무 사례' ? 'Cases' : 'Medi-Lawyer'} · ${section.group}`,
    title: section.label,
    lede: section.desc,
    seoTitle: `${section.label} — 미디어 센터 | 메디로이어`,
    seoDescription: section.desc,
    path: `/blog/${section.slug}`,
  };
}

/** @param {{ sectionSlug?: string|null }} props sectionSlug 가 없으면 전체 탭. */
export function MediaCenterView({ sectionSlug = null }) {
  const sectionMeta = sectionSlug ? getBlogSection(sectionSlug) : null;

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sort, setSort] = useState('recent');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE + 1); // hero 1 + grid 6

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // sentinel 의 IntersectionObserver 가 재연결되지 않도록 identity 를 고정한다.
  // 인라인 화살표로 두면 렌더마다 새 함수가 되고 → LoadMoreSentinel 의 effect 가 다시 돌고
  // → observe() 직후 초기 교차 판정이 비동기로 한 번 더 불린다. 그래서 sentinel 이 화면
  // 근처에 있으면 검색어를 한 글자 칠 때마다 목록이 PAGE_SIZE 씩 저절로 늘어난다.
  const handleLoadMore = useCallback(() => setVisibleCount((n) => n + PAGE_SIZE), []);

  const allPosts = useMemo(() => {
    const base = getBlogPostsBySection(sectionSlug);
    return sortPosts(searchPosts(base, debouncedQuery), sort);
  }, [sectionSlug, debouncedQuery, sort]);

  const visible = allPosts.slice(0, visibleCount);
  const hero = visible[0] ?? null;
  const rest = visible.slice(1);
  const hasMore = visible.length < allPosts.length;

  // 존재하지 않는 섹션 슬러그 → 전체로. 훅을 전부 호출한 뒤에 반환한다.
  if (sectionSlug && !sectionMeta) return <Navigate to="/blog" replace />;

  const heading = getHeading(sectionMeta);
  const totalCount = sectionMeta ? sectionMeta.count : blogPosts.length;
  const emptyLabel = sectionMeta ? sectionMeta.label : '미디어 센터';

  return (
    <>
      <Seo title={heading.seoTitle} description={heading.seoDescription} path={heading.path} />
      <main className={styles.main}>
        {/* 네이비 밴드는 .wrap 바깥에 둔다 — 안에 넣으면 배경이 1100px 에서 잘린다.
            안쪽에 .wrap 을 다시 넣어 글자만 본문과 같은 폭에 맞춘다. */}
        <header className={styles.masthead}>
          <div className="wrap">
            {sectionMeta && (
              <Link to="/blog" className={styles.crumb}>
                ← 미디어 센터 전체
              </Link>
            )}
            <Eyebrow dark>{heading.eyebrow}</Eyebrow>
            <h1 className={styles.title}>{heading.title}</h1>
            <p className={styles.lede}>{heading.lede}</p>

            <div className={styles.statRow}>
              <div className={styles.stat}>
                <span className={styles.statNum}>{String(totalCount).padStart(2, '0')}</span>
                <span className={styles.statLab}>{sectionMeta ? '기록된 글' : '발행된 글'}</span>
              </div>
              <div className={styles.statDivider} />
              <div className={styles.stat}>
                <span className={styles.statNum}>
                  {sectionMeta ? allPosts.length : blogSections.length}
                </span>
                <span className={styles.statLab}>{sectionMeta ? '현재 검색결과' : '섹션'}</span>
              </div>
            </div>
          </div>
        </header>

        <div className="wrap">
          <MediaTabs activeSlug={sectionMeta?.slug ?? null} />

          <div className={styles.toolbar}>
            <div className={styles.search}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4-4" strokeLinecap="round" />
              </svg>
              <input
                type="search"
                placeholder="제목·요약·변호사로 검색"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="검색"
              />
            </div>
            <div className={styles.sort}>
              <span className={styles.sortLabel}>{allPosts.length}개 결과</span>
              <span className={styles.sortDot}>·</span>
              <span className={styles.sortLabel}>정렬</span>
              <button
                type="button"
                className={`${styles.sortPill} ${sort === 'recent' ? styles.sortPillActive : ''}`}
                aria-pressed={sort === 'recent'}
                onClick={() => setSort('recent')}
              >
                최신순
              </button>
              <button
                type="button"
                className={`${styles.sortPill} ${sort === 'popular' ? styles.sortPillActive : ''}`}
                aria-pressed={sort === 'popular'}
                onClick={() => setSort('popular')}
              >
                인기순
              </button>
            </div>
          </div>

          {allPosts.length === 0 && (
            <div className={styles.empty}>{emptyLabel}에 아직 발행된 글이 없습니다.</div>
          )}

          {hero && <HeroPost post={hero} sectionLabel={getBlogSection(hero.section)?.label} />}

          {rest.length > 0 && (
            <div className={styles.grid}>
              {rest.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  sectionLabel={getBlogSection(post.section)?.label}
                />
              ))}
            </div>
          )}

          <LoadMoreSentinel hasMore={hasMore} onEnter={handleLoadMore} />
        </div>
      </main>
    </>
  );
}
